<template>
  <div class="modal fade receipting-modal" tabindex="-1" role="dialog" aria-hidden="true">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content border-0 shadow">
        <!-- Modal Header -->
        <div class="modal-header border-0 py-3 modal-gradient-header">
          <div class="d-flex align-items-center">
            <div class="modal-icon me-3">
              <i class="bx bx-check-shield text-white"></i>
            </div>
            <div>
              <h5 class="modal-title mb-0 fw-semibold text-white">Approved Application</h5>
              <small class="text-white opacity-75">{{ application.licenceBillNo }} - {{ application.businessName }}</small>
            </div>
          </div>
          <div class="d-flex align-items-center gap-2">
            <span class="badge bg-success rounded-pill px-3">Approved</span>
            <button type="button" class="btn-close btn-close-white ms-2" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
        </div>

        <!-- Modal Body -->
        <div class="modal-body p-0">
          <div class="modal-form-scroll">
            <!-- Application Timeline -->
            <div class="timeline-section bg-light py-3 px-4 border-bottom">
              <h6 class="fw-semibold text-muted mb-3 small text-uppercase">
                <i class="bx bx-git-branch me-1"></i> Application Progress
              </h6>
              <div class="timeline-wrapper">
                <div class="timeline-track">
                  <div v-for="(item, index) in applicationStatuses" :key="index"
                       class="timeline-item" :class="getTimelineClass(item)">
                    <div class="timeline-marker">
                      <i v-if="item.completed === 'true'" class="bx bx-check"></i>
                      <i v-else-if="item.completed === 'active'" class="bx bx-loader-circle bx-spin"></i>
                      <span v-else>{{ index + 1 }}</span>
                    </div>
                    <div class="timeline-content">
                      <div class="timeline-title">{{ item.status }}</div>
                      <div class="timeline-date" v-if="item.completed === 'true'">
                        {{ formatDate(item.updatedDate) }}
                      </div>
                      <div class="timeline-officer" v-if="item.issuingOfficer">
                        {{ item.issuingOfficer }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="p-4">
              <div class="row g-4">
                <!-- Business Details -->
                <div class="col-md-6">
                  <div class="info-card h-100">
                    <div class="info-card-header">
                      <i class="bx bx-briefcase-alt-2 text-primary"></i>
                      <span>Business Details</span>
                    </div>
                    <div class="info-card-body">
                      <div class="info-row">
                        <span class="info-label">Business Name</span>
                        <span class="info-value fw-semibold">{{ application.businessName || '-' }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Business ID</span>
                        <span class="info-value">{{ application.businessID || '-' }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Category</span>
                        <span class="info-value">{{ application.businessCategory || '-' }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Sub Category</span>
                        <span class="info-value small">{{ application.businessSubCategory || '-' }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Location Details -->
                <div class="col-md-6">
                  <div class="info-card h-100">
                    <div class="info-card-header">
                      <i class="bx bx-map text-success"></i>
                      <span>Location Details</span>
                    </div>
                    <div class="info-card-body">
                      <div class="info-row">
                        <span class="info-label">Sub County</span>
                        <span class="info-value">{{ application.subCountyName || '-' }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Ward</span>
                        <span class="info-value">{{ application.wardName || '-' }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Plot Number</span>
                        <span class="info-value">{{ application.plotNumber || '-' }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Fiscal Year</span>
                        <span class="info-value">{{ application.fiscalYear || '-' }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Licence Details -->
                <div class="col-md-6">
                  <div class="info-card h-100">
                    <div class="info-card-header bg-success-subtle">
                      <i class="bx bx-id-card text-success"></i>
                      <span>Licence Details</span>
                    </div>
                    <div class="info-card-body">
                      <div class="info-row">
                        <span class="info-label">Date Inspected</span>
                        <span class="info-value">{{ formatDate(application.dateInspected) }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Licence Category</span>
                        <span class="info-value">{{ application.licenceCategory || '-' }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Licence Sub Category</span>
                        <span class="info-value small">{{ application.licenceSubCategory || '-' }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Duration</span>
                        <span class="info-value">
                          <span class="badge bg-success">{{ application.licenceDuration || '-' }}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Invoice Details -->
                <div class="col-md-6">
                  <div class="info-card h-100">
                    <div class="info-card-header">
                      <i class="bx bx-receipt text-warning"></i>
                      <span>Licence Invoice</span>
                    </div>
                    <div class="info-card-body">
                      <div class="info-row">
                        <span class="info-label">Invoice No.</span>
                        <span class="info-value text-primary fw-semibold">{{ application.licenceBillNo || '-' }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Invoice Amount</span>
                        <span class="info-value fw-bold">{{ formatCurrency(application.licenceDetailAmount) }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Amount Paid</span>
                        <span class="info-value text-success">{{ formatCurrency(application.licenceReceiptAmount) }}</span>
                      </div>
                      <div class="info-row">
                        <span class="info-label">Balance</span>
                        <span class="info-value" :class="parseFloat(application.licenceBillBalance) > 0 ? 'text-danger fw-bold' : 'text-success'">
                          {{ formatCurrency(application.licenceBillBalance) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Inspection Notes -->
                <div class="col-12">
                  <div class="info-card">
                    <div class="info-card-header bg-info-subtle">
                      <i class="bx bx-search-alt text-info"></i>
                      <span>Inspection Notes</span>
                    </div>
                    <div class="info-card-body">
                      <div class="inspection-notes p-3 bg-light rounded">
                        <p class="mb-2">{{ application.comments || 'No inspection notes available.' }}</p>
                        <hr class="my-2">
                        <small class="text-muted">
                          <i class="bx bx-user me-1"></i> Inspected by:
                          <strong>{{ application.issuingOfficer || '-' }}</strong>
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Committee Review Outcome -->
                <div class="col-12">
                  <div class="approval-card">
                    <div class="approval-header">
                      <i class="bx bx-check-shield"></i>
                      <span>Committee Review Outcome</span>
                    </div>
                    <div class="approval-body">
                      <div class="row g-3">
                        <div class="col-md-6">
                          <div class="info-row">
                            <span class="info-label">Minutes Number</span>
                            <span class="info-value fw-semibold">{{ application.minutesNumber || '-' }}</span>
                          </div>
                        </div>
                        <div class="col-md-6">
                          <div class="info-row">
                            <span class="info-label">Minutes Document</span>
                            <span class="info-value">
                              <a v-if="application.minutesFile" :href="documentURL" target="_blank" class="text-primary">
                                <i class="bx bx-file me-1"></i> View Document
                              </a>
                              <span v-else class="text-muted">No document uploaded</span>
                            </span>
                          </div>
                        </div>
                      </div>
                      <div class="alert alert-success mt-3 mb-0">
                        <div class="d-flex align-items-center">
                          <i class="bx bx-check-circle font-size-24 me-3"></i>
                          <div>
                            <h6 class="mb-1">Application Approved</h6>
                            <p class="mb-0 small">This application has been duly approved as the applicant met all requirements under the Alcoholic Drinks Control Act.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer bg-light border-0 py-3">
          <button type="button" class="btn btn-light" data-bs-dismiss="modal">
            <i class="bx bx-x me-1"></i> Close
          </button>
          <button @click="printBill()" type="button" class="btn btn-outline-primary">
            <i class="bx bx-printer me-1"></i> Print Invoice
          </button>
          <button v-if="hasBalance" @click="receivePayment()" type="button" class="btn btn-warning text-dark" data-bs-dismiss="modal">
            <i class="bx bx-credit-card me-1"></i> Receive Payment
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { uploadsLiqour } from "@/api";

export default {
  name: "ApprovedDetailsModal",
  props: {
    application: {
      type: Object,
      default: () => ({})
    },
    applicationStatuses: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    documentURL() {
      return uploadsLiqour + '' + this.application.minutesFile;
    },
    hasBalance() {
      return parseFloat(this.application.licenceBillBalance) > 0;
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString || dateString === '-') return '-';
      return moment(dateString).format('DD MMM YYYY');
    },

    formatCurrency(amount) {
      if (!amount || isNaN(amount)) return 'KES 0';
      return 'KES ' + parseFloat(amount).toLocaleString('en-KE');
    },

    getTimelineClass(item) {
      if (item.completed === 'true') return 'completed';
      if (item.completed === 'active') return 'active';
      return 'pending';
    },

    printBill() {
      this.$emit('print-bill', this.application.licenceBillNo);
    },

    receivePayment() {
      this.$emit('receive-payment', this.application.licenceBillNo);
    }
  }
}
</script>

<style scoped>
/* Modal Header Gradient */
.modal-gradient-header {
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
}

.modal-icon {
  width: 45px;
  height: 45px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

/* Modal Scroll */
.modal-form-scroll {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

.modal-form-scroll::-webkit-scrollbar {
  width: 6px;
}

.modal-form-scroll::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.modal-form-scroll::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

/* Timeline */
.timeline-wrapper {
  overflow-x: auto;
  padding-bottom: 10px;
}

.timeline-track {
  display: flex;
  gap: 0;
  min-width: max-content;
}

.timeline-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 120px;
  position: relative;
  padding: 0 10px;
}

.timeline-item:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 15px;
  left: 60%;
  width: 80%;
  height: 2px;
  background: #e9ecef;
}

.timeline-item.completed:not(:last-child)::after {
  background: #28a745;
}

.timeline-marker {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  background: #e9ecef;
  color: #6c757d;
  z-index: 1;
}

.timeline-item.completed .timeline-marker {
  background: #28a745;
  color: white;
}

.timeline-item.active .timeline-marker {
  background: #007bff;
  color: white;
}

.timeline-content {
  text-align: center;
  margin-top: 8px;
}

.timeline-title {
  font-size: 0.75rem;
  font-weight: 600;
  color: #495057;
}

.timeline-date {
  font-size: 0.65rem;
  color: #6c757d;
}

.timeline-officer {
  font-size: 0.65rem;
  color: #28a745;
}

/* Info Card */
.info-card {
  background: #f8f9fa;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e9ecef;
}

.info-card-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: white;
  font-weight: 600;
  font-size: 0.875rem;
  color: #495057;
  border-bottom: 2px solid #e9ecef;
}

.info-card-header i {
  font-size: 1.25rem;
}

.info-card-body {
  padding: 1rem;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem 0;
  border-bottom: 1px dashed #e9ecef;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  color: #6c757d;
  font-size: 0.8rem;
}

.info-value {
  font-size: 0.85rem;
  text-align: right;
  max-width: 60%;
}

/* Approval Card */
.approval-card {
  background: white;
  border-radius: 12px;
  border: 2px solid #28a745;
  overflow: hidden;
}

.approval-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  font-weight: 600;
  color: #155724;
}

.approval-header i {
  font-size: 1.5rem;
}

.approval-body {
  padding: 1.5rem;
}

/* Backgrounds */
.bg-success-subtle { background-color: rgba(25, 135, 84, 0.1); }
.bg-info-subtle { background-color: rgba(13, 202, 240, 0.1); }
</style>